import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { SeasonApiService } from '@bcmApiServices/seasons.api-service';
import { TenantRelationsApiService } from '@bcmApiServices/tenant-relations.api-service';
import { WinterStorageApiService } from '@bcmApiServices/winter-storage.api-service';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { DynamicPriceRulesEntityId } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-entity-id';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { BcmSettingsSectionName } from '@shared/models/bcm-settings';
import { Season } from '@shared/models/seasons';

import { BookingV2RelationOption, BookingV2WinterStorageOption } from '../dtos/booking-v2.dtos';
import { DynamicPriceDurations, EvaluatedPrice, boatDimensionsOf, durationsOf, evaluateProductPrice } from '../utils/booking-v2-dynamic-price-evaluation';
import { toRelationOption } from '../utils/booking-v2-option.mappers';
import { effectivePricingRelationId, needsPricingRelation } from '../utils/booking-v2-pricing-relation';
import { withKey } from '../utils/booking-v2-record';
import { toWinterStorageOption } from '../utils/booking-v2-winter-storage';
import { BookingV2Facade } from './booking-v2.facade';

// The identifying inputs of one evaluation pass (ids/dimensions/durations only —
// never the resolved prices, so writing prices back can't re-trigger evaluation).
interface RelationPricingContext {
    relationId: number | null;
    hasDynamic: boolean;
    boatId: number | null;
    boatLength: number | null;
    boatWidth: number | null;
    berthId: number | null;
    pierId: number | null;
    seasonId: number | null;
    winterStorageId: number | null;
    // productId → relation the rule is evaluated with, when it is not the booking's (#7044).
    pricingRelationIds: [string, number][];
    durations: DynamicPriceDurations;
}

// Resolves the dynamic prices of a relation's Leistungen against the booking
// context (boat / berth / relation / season) and writes the backend-evaluated
// `rulePrice` back onto the facade's selectedRelation products — so the person-step
// preview, the Rechnungsversand summary AND the billing all use the real price
// instead of the static catalog base. Also owns the booking-wide season list,
// the date-based season preselect and the COMBINED season picker/gating across
// relation AND services (the season is a pricing input, not persisted) — the
// dynamic services are priced by the sibling BookingV2ServicePricingService.
//
// Provided at the dialog level so it shares the dialog's BookingV2Facade instance.
@Injectable()
export class BookingV2RelationPricingService {

    private readonly facade = inject(BookingV2Facade);
    private readonly productsApi = inject(ProductsApiService);
    private readonly seasonsApi = inject(SeasonApiService);
    private readonly winterStoragesApi = inject(WinterStorageApiService);
    private readonly tenantRelationsApi = inject(TenantRelationsApiService);
    private readonly settingsFacade = inject(BcmSettingsFacade);

    // Guest-hours config the overnights calc needs (relation vesting = booking dates).
    // Plain read — `settings()` is not a signal; it is loaded long before the dialog.
    private timeUnit() {
        return this.settingsFacade.settings()?.[BcmSettingsSectionName.DefaultBerthReservationTimeUnit] ?? null;
    }

    readonly seasons = signal<Season[]>([]);

    readonly winterStorages = signal<BookingV2WinterStorageOption[]>([]);

    // All Beziehungen of the tenant — the person step's relation picker and the
    // pricing-relation pickers (relation card + services tab) read the same list.
    // Mutable element type: `<p-select [options]>` rejects readonly arrays.
    readonly relationOptions = signal<BookingV2RelationOption[]>([]);

    // Entity ids referenced by the SELECTED RELATION's dynamic Leistungen — drives
    // the relation card's season picker + warnings (shown next to the relation).
    private readonly relationRequiredEntities = computed<readonly string[]>(() =>
        this.facade.selectedRelation()?.requiredEntities ?? [],
    );

    // Entity ids referenced by the dynamic products added in the SERVICES tab —
    // drives the services tab's season picker + warnings (shown next to the lines).
    private readonly serviceRequiredEntities = computed<readonly string[]>(() => [
        ...new Set(
            this.facade.services()
                .filter(s => s.hasDynamicPrice)
                .flatMap(s => s.requiredEntities ?? []),
        ),
    ]);

    // Show the season picker per context only when that context's rules reference the
    // season and seasons exist. The picker binds the same global selectedSeason.
    readonly relationShowSeasonPicker = computed(() =>
        this.seasons().length > 0 && this.relationRequiredEntities().includes(DynamicPriceRulesEntityId.Season),
    );
    // The season is global (one per booking). If the relation card already exposes
    // the picker, the services tab must NOT show a second one for the same value —
    // the already-chosen season applies to the dynamic services too. So the service
    // picker only appears when a service needs the season AND the relation doesn't
    // already provide the picker (e.g. no relation, or its rules don't branch on it).
    readonly serviceShowSeasonPicker = computed(() =>
        !this.relationShowSeasonPicker()
        && this.seasons().length > 0
        && this.serviceRequiredEntities().includes(DynamicPriceRulesEntityId.Season),
    );

    // A season is required by EITHER context but none is chosen → the price would
    // silently fall back. Save is blocked on this (commit service) and the relevant
    // context's picker shows the required `*`.
    readonly seasonMissing = computed(() =>
        (this.relationShowSeasonPicker() || this.serviceShowSeasonPicker()) && this.facade.selectedSeason() == null,
    );

    // Lagerplatz picker — same placement rules as the season (relation card vs.
    // services tab, deduped). Shown only when that context's rules branch on the
    // Lagerplatz and storages exist; binds the global selectedWinterStorage.
    readonly relationShowWinterStoragePicker = computed(() =>
        this.winterStorages().length > 0 && this.relationRequiredEntities().includes(DynamicPriceRulesEntityId.WinterStorage),
    );
    readonly serviceShowWinterStoragePicker = computed(() =>
        !this.relationShowWinterStoragePicker()
        && this.winterStorages().length > 0
        && this.serviceRequiredEntities().includes(DynamicPriceRulesEntityId.WinterStorage),
    );

    // A Lagerplatz rule applies but none is chosen → the relevant picker shows the
    // required `*` + hint. Unlike the season this is NOT a Save-gate (mirrors the
    // legacy widget); on edit the storage is recovered from the saved provenance.
    readonly winterStorageMissing = computed(() =>
        (this.relationShowWinterStoragePicker() || this.serviceShowWinterStoragePicker())
        && this.facade.selectedWinterStorageId() == null,
    );

    // Required entities the wizard cannot satisfy at all → a visible hint instead of
    // a silently wrong (fallback) price: a berth-dependent rule with no berth chosen.
    // (The Lagerplatz now has its own picker, so it is no longer a warning.)
    readonly relationPriceWarnings = computed(() => this.warningsFrom(this.relationRequiredEntities()));
    readonly servicePriceWarnings = computed(() => this.warningsFrom(this.serviceRequiredEntities()));

    private warningsFrom(req: readonly string[]): ('berthMissing')[] {
        const warnings: ('berthMissing')[] = [];
        if (req.includes(DynamicPriceRulesEntityId.Berth) && this.facade.selectedBerthId() == null) {
            warnings.push('berthMissing');
        }
        return warnings;
    }

    // The identifying inputs of an evaluation — deliberately NOT the resolved
    // prices, so writing prices back never re-triggers the evaluation (no loop).
    private readonly context = computed<RelationPricingContext>(() => {
        const relation = this.facade.selectedRelation();
        const boat = this.facade.selectedBoat();
        const dr = this.facade.dateRange();
        return {
            relationId: relation?.id ?? null,
            hasDynamic: (relation?.products ?? []).some(p => p.hasDynamicPrice),
            boatId: boat?.origin === 'existing' ? boat.id : null,
            boatLength: boat?.length ?? null,
            boatWidth: boat?.width ?? null,
            berthId: this.facade.selectedBerthId(),
            // Steg of the primary berth — the backend needs it explicitly for a
            // pier rule (it does not derive the pier from the berth).
            pierId: this.facade.selectedBerths()[0]?.pierId ?? null,
            seasonId: this.facade.selectedSeason()?.id ?? null,
            winterStorageId: this.facade.selectedWinterStorageId(),
            pricingRelationIds: Object.entries(this.facade.effectiveRelationPricingRelationIds())
                .sort(([a], [b]) => Number(a) - Number(b)),
            // Duration/traveler inputs for night-/day-/traveler-based rule factors.
            durations: durationsOf(dr.from, dr.to, this.facade.personsOnBoard(), this.timeUnit()),
        };
    });

    constructor() {
        this.seasonsApi.getAllSeasons()
            .pipe(takeUntilDestroyed())
            .subscribe(seasons => this.seasons.set(seasons));

        // Loaded once for the Lagerplatz picker; only relevant when a rule branches
        // on it, but cheap and lets the picker render instantly when it does.
        this.winterStoragesApi.getAll()
            .pipe(takeUntilDestroyed())
            .subscribe(list => this.winterStorages.set(list.map(toWinterStorageOption)));

        this.tenantRelationsApi.getAll()
            .pipe(
                map(list => list.map(toRelationOption)),
                catchError(() => of<BookingV2RelationOption[]>([])),
                takeUntilDestroyed(),
            )
            .subscribe(list => this.relationOptions.set(list));

        // Re-price the relation's rule-based Leistungen whenever the context changes.
        // debounceTime(0) coalesces a synchronous burst (e.g. selecting a relation
        // then the preselect effect setting a season) into ONE evaluation — no
        // intermediate fallback-price flash; distinctUntilChanged then drops no-ops.
        toObservable(this.context)
            .pipe(
                debounceTime(0),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                switchMap(ctx => this.evaluate(ctx)),
                takeUntilDestroyed(),
            )
            .subscribe();

        // Preselect the season covering the booking start once the picker is
        // relevant and the user has not chosen one — also recovers the season on
        // edit (it is not stored on the booking). Picking it re-triggers pricing.
        effect(() => {
            const anyPicker = this.relationShowSeasonPicker() || this.serviceShowSeasonPicker();
            if (!anyPicker || this.facade.selectedSeason()) {
                return;
            }
            const from = this.facade.dateRange().from;
            const match = from ? this.seasons().find(s => s.isDateInSeason(from)) : null;
            if (match) {
                this.facade.selectedSeason.set(match);
            }
        });
    }

    setSeason(season: Season | null): void {
        this.facade.selectedSeason.set(season);
    }

    setWinterStorage(option: BookingV2WinterStorageOption | null): void {
        this.facade.selectedWinterStorage.set(option);
    }

    // Whether the rule of this product/line needs a Beziehung other than the booking's own.
    needsPricingRelation(item: { hasDynamicPrice?: boolean; tenantRelationCriteriaIds?: readonly number[] }): boolean {
        return needsPricingRelation(
            item.hasDynamicPrice,
            item.tenantRelationCriteriaIds,
            this.facade.selectedRelation()?.id ?? null,
        );
    }

    // The relations a rule names, in the order of the tenant's relation list.
    pricingRelationOptions(criteriaIds: readonly number[] | undefined): BookingV2RelationOption[] {
        const ids = new Set(criteriaIds ?? []);
        return this.relationOptions().filter(r => ids.has(r.id));
    }

    setPricingRelation(productId: number, relationId: number | null): void {
        this.facade.relationPricingRelationIds.update(current => withKey(current, productId, relationId));
    }

    private evaluate(ctx: RelationPricingContext): Observable<unknown> {
        const relation = this.facade.selectedRelation();
        const products = (relation?.products ?? []).filter(p => p.hasDynamicPrice);
        if (!ctx.hasDynamic || !relation || !products.length) {
            return of(null);
        }
        const boatDimensions = boatDimensionsOf(ctx.boatLength, ctx.boatWidth);
        const picked = new Map(ctx.pricingRelationIds.map(([productId, relationId]) => [Number(productId), relationId]));

        const requests: Observable<EvaluatedPrice<number>>[] = products.map(p =>
            evaluateProductPrice(this.productsApi, {
                key: p.productId,
                productId: p.productId,
                quantity: p.quantity,
                parameters: {
                    tenantRelation: {
                        id: effectivePricingRelationId(p.hasDynamicPrice, p.tenantRelationCriteriaIds, picked.get(p.productId), relation.id),
                    },
                    boat: ctx.boatId != null ? { id: ctx.boatId } : undefined,
                    berth: ctx.berthId != null ? { id: ctx.berthId } : undefined,
                    pier: ctx.pierId != null ? { id: ctx.pierId } : undefined,
                    season: ctx.seasonId != null ? { id: ctx.seasonId } : undefined,
                    winterStorage: ctx.winterStorageId != null ? { id: ctx.winterStorageId } : undefined,
                },
                boatDimensions,
                durations: ctx.durations,
            }),
        );

        // forkJoin over an array variable widens to Observable<unknown> in this
        // rxjs version; the cast is sound — `requests` is typed Observable<EvaluatedPrice>[].
        return forkJoin(requests).pipe(tap(results => this.applyPrices(results as EvaluatedPrice<number>[])));
    }

    private applyPrices(results: EvaluatedPrice<number>[]): void {
        const byId = new Map<number, { price: number; ruleName: string | null; dynamicPrice: Partial<BcmDynamicPriceDto> | null }>();
        for (const r of results) {
            if (r && r.price != null) {
                byId.set(r.key, { price: r.price, ruleName: r.ruleName, dynamicPrice: r.dynamicPrice });
            }
        }
        if (!byId.size) {
            return;
        }
        this.facade.selectedRelation.update(relation => {
            if (!relation?.products) {
                return relation;
            }
            return {
                ...relation,
                products: relation.products.map(p => {
                    const r = byId.get(p.productId);
                    return r ? { ...p, price: r.price, dynamicRuleName: r.ruleName, dynamicPrice: r.dynamicPrice } : p;
                }),
            };
        });
    }
}
